/**
 * Canonical case-number display helper.
 *
 * Every case row, push, alert, breadcrumb or label across the platform must
 * show the case number WITH its type prefix (AS, Crl, SC, OP, WP, MAC, etc.)
 * — never the bare numeric portion.
 *
 * Priority order:
 *   1. `original_suit_number` (already prefixed, eg "OP 1435/1993") — best
 *   2. Compose: `${ecourts_case_type || type || category} ${court_case_number}`
 *   3. `case_number` (legacy/manual entries)
 *   4. `internal_number` (firm-side ID, eg "CS-00043")
 *   5. "—"
 *
 * Pass either a `Case` doc OR a `Hearing` doc that has been enriched with
 * case fields via the My Week JOIN (`case_number`, `cnr`, `case_title`).
 */
function pick(...vals) {
    for (const v of vals) {
        if (v !== undefined && v !== null && String(v).trim() !== "") return String(v).trim();
    }
    return null;
}

/** Returns the display case number with its type prefix. */
export function caseDisplayNumber(c) {
    if (!c) return "—";
    // 1. Already-prefixed canonical
    const osn = pick(c.original_suit_number);
    if (osn) return osn;
    // 2. Compose prefix + numeric
    const prefix = pick(c.ecourts_case_type, c.ecourts_case_type_raw, c.case_type, c.type);
    const num = pick(c.court_case_number, c.case_number);
    if (prefix && num) return `${prefix} ${num}`;
    if (num) return num;
    // 3. Manual / firm-side fallbacks
    return pick(c.case_number, c.internal_number) || "—";
}

/** Returns `caseDisplayNumber` AND the CNR joined with " · " for compact rows. */
export function caseIdsLabel(c) {
    const num = caseDisplayNumber(c);
    const cnr = pick(c?.cnr);
    if (num && num !== "—" && cnr) return `${num} · ${cnr}`;
    if (cnr) return cnr;
    return num;
}

/** Returns just the bare numeric portion (no prefix), used for sort keys. */
export function caseSortKey(c) {
    return pick(c?.court_case_number, c?.case_number, c?.internal_number) || "";
}
